import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Star,
  Search,
  PlusCircle,
  Sparkles,
  MapPin,
  User,
  Quote,
  ShieldCheck,
  Edit,
  Trash2,
  Check,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminSidebar from '../../components/AdminSidebar';
import { Modal, ConfirmDialog } from '../../components/Modal';
import EmptyState from '../../components/EmptyState';
import api from '../../config/api';

const statusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Approved', value: 'approved' },
  { label: 'Pending', value: 'pending' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Archived', value: 'archived' },
];

const statusStyles = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700',
  archived: 'bg-gray-100 text-gray-600',
};

const initialFormState = {
  displayName: '',
  relationship: '',
  location: '',
  childName: '',
  childAge: '',
  rating: 5,
  headline: '',
  content: '',
  status: 'approved',
  featured: true,
  source: 'Parent interview',
  avatarUrl: '',
  userId: '',
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [editingReview, setEditingReview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const getErrorMessage = useCallback((error, fallbackMessage) => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return error?.message || fallbackMessage;
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 12,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (search.trim()) {
        params.search = search.trim();
      }
      const { data } = await api.get('/admin/reviews', { params });
      if (data.success) {
        setReviews(data.reviews);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error, 'Failed to load reviews'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, getErrorMessage]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const openCreateModal = () => {
    setFormState(initialFormState);
    setEditingReview(null);
    setShowModal(true);
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormState({
      displayName: review.display_name || '',
      relationship: review.relationship || '',
      location: review.location || '',
      childName: review.child_name || '',
      childAge: review.child_age || '',
      rating: review.rating || 5,
      headline: review.headline || '',
      content: review.content || '',
      status: review.status || 'pending',
      featured: Boolean(review.featured),
      source: review.source || '',
      avatarUrl: review.avatar_url || '',
      userId: review.user_id || '',
    });
    setShowModal(true);
  };

  const handleSaveReview = async () => {
    try {
      setSaving(true);
      const payload = {
        displayName: formState.displayName.trim(),
        relationship: formState.relationship || null,
        location: formState.location || null,
        childName: formState.childName || null,
        childAge: formState.childAge || null,
        rating: Number(formState.rating),
        headline: formState.headline || null,
        content: formState.content.trim(),
        status: formState.status,
        featured: Boolean(formState.featured),
        source: formState.source || null,
        avatarUrl: formState.avatarUrl || null,
        userId: formState.userId || null,
      };

      if (editingReview) {
        await api.put(`/admin/reviews/${editingReview.id}`, payload);
        toast.success('Review updated');
      } else {
        await api.post('/admin/reviews', payload);
        toast.success('Review created');
      }

      setShowModal(false);
      setEditingReview(null);
      fetchReviews();
    } catch (error) {
      const message = error.response?.data?.errors?.[0]?.msg || error.response?.data?.message || 'Unable to save review';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete review'));
    }
  };

  const handleStatusChange = async (review, nextStatus) => {
    if (review.status === nextStatus) return;
    try {
      await api.patch(`/admin/reviews/${review.id}/status`, { status: nextStatus, featured: review.featured });
      toast.success(`Marked as ${nextStatus}`);
      fetchReviews();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to update status'));
    }
  };

  const toggleFeatured = async (review) => {
    try {
      await api.patch(`/admin/reviews/${review.id}/status`, {
        status: review.status,
        featured: !review.featured,
      });
      toast.success(!review.featured ? 'Review spotlighted' : 'Review removed from spotlight');
      fetchReviews();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update featured flag'));
    }
  };

  const displayedReviews = useMemo(() => reviews, [reviews]);

  const renderStars = (rating = 5) =>
    [...Array(5)].map((_, idx) => (
      <Star
        key={idx}
        className={`h-4 w-4 ${idx < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));

  return (
    <AdminSidebar>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <p className="uppercase text-xs tracking-[0.5em] text-purple-500">Social proof</p>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Review Management</h1>
            <p className="text-gray-600 mt-1">
              Curate trusted testimonials, spotlight top voices, and keep the story authentic.
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-transform hover:-translate-y-0.5"
          >
            <PlusCircle className="h-5 w-5" />
            Add Review
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setPage(1);
                  }}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === option.value
                      ? 'bg-purple-600 text-white shadow'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by parent, child, or quote..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl shadow-lg p-6 border border-purple-50 animate-pulse h-80"
              >
                <div className="h-6 bg-purple-100 rounded w-32 mb-4" />
                <div className="h-4 bg-gray-100 rounded w-48 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                <div className="h-4 bg-gray-100 rounded w-5/6 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-4/6 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-6" />
                <div className="h-10 bg-gray-50 rounded" />
              </div>
            ))}
          </div>
        ) : displayedReviews.length === 0 ? (
          <EmptyState
            icon={<Quote className="h-7 w-7" />}
            title="No reviews yet"
            description="Collect your first story by interviewing parents or importing quotes from surveys."
            action={{
              label: 'Add review',
              onClick: openCreateModal,
            }}
          />
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedReviews.map((review) => (
                <div key={review.id} className="bg-white rounded-2xl shadow-lg border border-purple-50 p-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center text-purple-800 font-bold">
                        {review.display_name?.[0] ?? 'P'}
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900">{review.display_name}</p>
                        {review.relationship && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {review.relationship}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleFeatured(review)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        review.featured ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <Sparkles className="h-3 w-3" />
                      {review.featured ? 'Spotlighted' : 'Feature'}
                    </button>
                  </div>

                  <div className="flex items-center gap-2">{renderStars(review.rating)}</div>

                  {review.headline && (
                    <h3 className="text-lg font-bold text-gray-900">{review.headline}</h3>
                  )}

                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-5">“{review.content}”</p>

                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {review.location && (
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
                        <MapPin className="h-3 w-3" />
                        {review.location}
                      </span>
                    )}
                    {review.child_name && (
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full">
                        <ShieldCheck className="h-3 w-3" />
                        {review.child_name}
                        {review.child_age ? ` • ${review.child_age}` : ''}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${statusStyles[review.status] || 'bg-gray-100 text-gray-600'}`}>
                      {review.status?.toUpperCase()}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleStatusChange(review, review.status === 'approved' ? 'pending' : 'approved')}
                        className="text-green-600 hover:text-green-800"
                        title={review.status === 'approved' ? 'Move to pending' : 'Approve'}
                      >
                        <Check className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(review)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setDeleteTarget(review);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {pagination.pages > 1 && (
              <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages} • {pagination.total} total reviews
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, pagination.pages))}
                    disabled={page === pagination.pages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

      {/* Add/Edit Review Modal */}
      {showModal && (
        <Modal
          title={editingReview ? 'Edit Review' : 'Add Review'}
          onClose={() => {
            setShowModal(false);
            setEditingReview(null);
          }}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                <input
                  type="text"
                  value={formState.displayName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <input
                  type="text"
                  value={formState.relationship}
                  onChange={(e) => setFormState((prev) => ({ ...prev, relationship: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formState.location}
                  onChange={(e) => setFormState((prev) => ({ ...prev, location: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Child name</label>
                <input
                  type="text"
                  value={formState.childName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, childName: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Child age</label>
                <input
                  type="text"
                  value={formState.childAge}
                  onChange={(e) => setFormState((prev) => ({ ...prev, childAge: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <select
                  value={formState.rating}
                  onChange={(e) => setFormState((prev) => ({ ...prev, rating: Number(e.target.value) }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                >
                  {[5, 4, 3, 2, 1].map((value) => (
                    <option key={value} value={value}>
                      {value} Stars
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formState.status}
                  onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                >
                  {statusOptions
                    .filter((option) => option.value !== 'all')
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
              <input
                type="text"
                value={formState.headline}
                onChange={(e) => setFormState((prev) => ({ ...prev, headline: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Story</label>
              <textarea
                value={formState.content}
                onChange={(e) => setFormState((prev) => ({ ...prev, content: e.target.value }))}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                rows={5}
              />
              <p className="text-xs text-gray-400 mt-1">Aim for at least 20 words so we can quote the highlight.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={formState.source}
                  onChange={(e) => setFormState((prev) => ({ ...prev, source: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Avatar URL</label>
                <input
                  type="url"
                  value={formState.avatarUrl}
                  onChange={(e) => setFormState((prev) => ({ ...prev, avatarUrl: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Linked user ID (optional)</label>
                <input
                  type="number"
                  min="1"
                  value={formState.userId}
                  onChange={(e) => setFormState((prev) => ({ ...prev, userId: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formState.featured}
                onChange={(e) => setFormState((prev) => ({ ...prev, featured: e.target.checked }))}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded"
              />
              Spotlight on homepage
            </label>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingReview(null);
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReview}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Saving...' : editingReview ? 'Save changes' : 'Publish review'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteDialog && deleteTarget && (
        <ConfirmDialog
          title="Delete review"
          description="This testimonial will be removed from every surface. Continue?"
          confirmLabel="Delete review"
          onConfirm={() => {
            handleDelete(deleteTarget.id);
            setShowDeleteDialog(false);
            setDeleteTarget(null);
          }}
          onCancel={() => {
            setShowDeleteDialog(false);
            setDeleteTarget(null);
          }}
        />
      )}
    </AdminSidebar>
  );
};

export default AdminReviews;


